import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import './Tutorial.css';

/**
 * First-run onboarding overlay. Walks a new operator through the 5 most
 * important screens (Dashboard → Scenarios → Replays → Divergence →
 * Attestations). The overlay is a simple modal card rather than a
 * product-tour library — no extra dependency, and the copy stays in sync
 * with the inline GuidancePanel entries.
 *
 * The overlay shows automatically on first load (when
 * `localStorage.getItem('sentinel.tutorial.completed')` is not set) and can
 * be re-triggered from the "Start tutorial" button in the layout top-bar.
 */

interface TutorialStep {
  key: string;
  route: string;
  title: string;
  body: string;
}

const STEPS: TutorialStep[] = [
  {
    key: 'dashboard',
    route: '/dashboard',
    title: 'Start on the Dashboard',
    body: 'Parity, drift, replays and pending sign-offs for the selected window — your day always starts here.',
  },
  {
    key: 'scenarios',
    route: '/scenarios',
    title: 'Know your scenario catalogue',
    body: 'Every typology is a versioned scenario — Draft for editing, Published for production, Retired for history.',
  },
  {
    key: 'replays',
    route: '/replays',
    title: 'Kick off a replay',
    body: 'A replay regenerates alerts from a frozen snapshot. Pick a scenario + window + reference snapshot and go.',
  },
  {
    key: 'divergence',
    route: '/divergence',
    title: 'Triage divergences',
    body: 'Matched / Missed / Extra / Threshold-drift — four buckets show exactly how the replay disagreed with history.',
  },
  {
    key: 'attestations',
    route: '/attestations',
    title: 'Seal + sign-off',
    body: '3-of-5 HSM-anchored signatures seal evidence for the regulator. Verify, download, or start a new attestation.',
  },
];

const COMPLETED_KEY = 'sentinel.tutorial.completed';

function readCompleted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(COMPLETED_KEY) === '1';
  } catch {
    return true;
  }
}

function writeCompleted(value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(COMPLETED_KEY, '1');
    else window.localStorage.removeItem(COMPLETED_KEY);
  } catch {
    // storage blocked — tutorial simply re-appears next session.
  }
}

/**
 * Expose a tiny event bus so the top-bar "Start tutorial" button can open
 * the overlay without prop-drilling through Layout.
 */
const OPEN_EVENT = 'sentinel:tutorial-open';

export function openTutorial() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export default function Tutorial() {
  const [open, setOpen] = useState<boolean>(false);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open on first load, but only when the user lands on the root or
  // the dashboard — opening on another deep-linked screen (or during a
  // Playwright validator run) would hijack their navigation intent. We
  // also skip /auth so the tutorial never covers the sign-in form.
  useEffect(() => {
    if (readCompleted()) return;
    const path = location.pathname;
    if (path.startsWith('/auth')) return;
    if (path !== '/' && path !== '/dashboard') return;
    const t = window.setTimeout(() => setOpen(true), 250);
    return () => window.clearTimeout(t);
    // Only run once on mount — route changes shouldn't re-open the overlay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for the global "open" event from the top-bar button.
  useEffect(() => {
    const onOpen = () => {
      setStepIndex(0);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Navigate to the current step's route when stepIndex changes. We track
  // which index we last navigated for so that pure route changes (the
  // user clicking a sidebar link or Playwright calling `page.goto`)
  // don't re-trigger a navigate from the tutorial — that would loop.
  const lastNavigatedStep = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      lastNavigatedStep.current = null;
      return;
    }
    if (lastNavigatedStep.current === stepIndex) return;
    const step = STEPS[stepIndex];
    if (!step) return;
    lastNavigatedStep.current = stepIndex;
    if (location.pathname !== step.route) {
      navigate(step.route, { replace: true });
    }
    // intentionally skip location deps — we navigate only on step change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepIndex, navigate]);

  // Track which route the tutorial has actually *settled* on. This is
  // distinct from `stepIndex` — React Router's `navigate` is asynchronous,
  // so between `setStepIndex(s + 1)` and the pathname updating there's a
  // render where stepIndex is advanced but pathname is still the previous
  // step. Without this, the "user left the route → close" effect fires
  // on that render and slams the overlay shut after every click.
  const settledRoute = useRef<string | null>(null);

  // If the user navigates away from the step's route *after* we settled
  // on it (sidebar click, Playwright `page.goto`), close the overlay so
  // we don't fight their navigation intent. They can re-open from the
  // top-bar button.
  useEffect(() => {
    if (!open) return;
    const step = STEPS[stepIndex];
    if (!step) return;
    // First: record when we've actually landed on the step's route. A
    // render where pathname matches the step is the "settled" moment.
    if (location.pathname === step.route) {
      settledRoute.current = step.route;
      return;
    }
    // We haven't settled on this step's route yet (the navigate effect
    // above either hasn't fired or the router hasn't applied its new
    // pathname). Do NOT close — the user hasn't done anything, React
    // Router is simply mid-transition.
    if (settledRoute.current !== step.route) return;
    // We previously saw pathname match this step, and now it's drifted
    // — the user truly left. Close.
    setOpen(false);
  }, [location.pathname, open, stepIndex]);

  // Reset the settled-route tracker whenever the overlay opens or the
  // step advances, so the next step's navigate gets a clean slate.
  useEffect(() => {
    settledRoute.current = null;
  }, [open, stepIndex]);

  const close = useCallback(
    (markComplete: boolean) => {
      setOpen(false);
      if (markComplete) writeCompleted(true);
    },
    [],
  );

  const advance = useCallback(() => {
    setStepIndex((s) => {
      if (s >= STEPS.length - 1) {
        close(true);
        return s;
      }
      return s + 1;
    });
  }, [close]);

  const back = useCallback(() => {
    setStepIndex((s) => Math.max(0, s - 1));
  }, []);

  if (!open) return null;
  const step = STEPS[stepIndex];
  if (!step) return null;
  const isLast = stepIndex === STEPS.length - 1;
  const isFirst = stepIndex === 0;

  return (
    <div
      className="tutorial-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Sentinel onboarding tutorial"
      data-testid="tutorial-overlay"
      onClick={() => close(false)}
    >
      <div
        className="tutorial-card"
        onClick={(e) => e.stopPropagation()}
        data-testid="tutorial-card"
      >
        <header className="tutorial-card__head">
          <div className="eyebrow">
            § TUTORIAL · STEP {stepIndex + 1} / {STEPS.length}
          </div>
          <h2 className="tutorial-card__title serif">{step.title}</h2>
          <p className="tutorial-card__route mono">{step.route}</p>
        </header>

        <p className="tutorial-card__body">{step.body}</p>

        <div className="tutorial-card__progress" aria-hidden>
          {STEPS.map((s, idx) => (
            <span
              key={s.key}
              className={`tutorial-dot${idx === stepIndex ? ' active' : ''}${idx < stepIndex ? ' done' : ''}`}
            />
          ))}
        </div>

        <footer className="tutorial-card__footer">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => close(true)}
            data-testid="tutorial-skip"
            title="Skip the tutorial — you can re-open it from the top-bar."
          >
            Skip
          </button>
          <span style={{ flex: 1 }} />
          {!isFirst && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={back}
              data-testid="tutorial-back"
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={advance}
            data-testid="tutorial-next"
          >
            {isLast ? 'Finish' : 'Next'}
          </button>
        </footer>
      </div>
    </div>
  );
}
